import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { BugAntIcon } from '@heroicons/react/24/outline';

const DebugLoggerNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const logLevel = data.config?.logLevel || 'info';

  const getLevelColor = (level: string) => {
    const colors: Record<string, string> = {
      debug: 'bg-gray-500',
      info: 'bg-blue-500',
      warn: 'bg-yellow-500',
      error: 'bg-red-500'
    };
    return colors[level] || 'bg-emerald-500';
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'error': return '🔴';
      case 'warn': return '🟡';
      case 'info': return '🔵';
      case 'debug': return '⚪';
      default: return '📝';
    }
  };

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-emerald-400 to-emerald-500 border-2 transition-all duration-200 ${
      selected ? 'border-emerald-600 shadow-xl scale-105' : 'border-emerald-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <BugAntIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-emerald-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Log level indicator */}
          <div className="flex items-center mt-1 space-x-1">
            <span className="text-xs">{getLevelIcon(logLevel)}</span>
            <span className="text-xs text-emerald-100 capitalize">
              {logLevel} szint
            </span>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Execution data preview */}
      {data.executionData && (
        <div className="mt-2 text-xs text-emerald-100 bg-black bg-opacity-20 px-2 py-1 rounded max-h-20 overflow-y-auto">
          <strong>Debug output:</strong>
          <pre className="whitespace-pre-wrap">
            {typeof data.executionData === 'string' 
              ? data.executionData 
              : JSON.stringify(data.executionData, null, 2)}
          </pre>
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-emerald-600 border-2 border-white"
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-emerald-600 border-2 border-white"
      />
    </div>
  );
};

export default DebugLoggerNode;
